import { Card, CardBody, CardHeader, CardHeading, Loading, isSuccess, useToasts } from "@dgs/core";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Formik } from "formik";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { contactViewService } from "../shared/api/contactViewService";
import { userService } from "../shared/api/userService";
import { ProfileSettingsResource, VCardFieldName } from "../shared/types/profileSettings";
import { ProfileSettingsForm } from "./ProfileSettingsForm";
import { profileSettingsService } from "./profileSettingsService";

const StyledCard = styled(Card)`
	margin: ${({ theme }) => theme.spacing(6)};
`;

const useValidate = () => {
	const { t } = useTranslation();

	return (values: ProfileSettingsResource) => {
		const errors: { [K in keyof ProfileSettingsResource]?: any } = {};

		if (!values.defaultContactView) {
			errors.defaultContactView = t("Field is required");
		} else if (!values.defaultContactView.containsPrimaryContactAttributes) {
			errors.defaultContactView = t(
				"The contact view must contain the first name, last name, primary email and primary company.",
			);
		}

		const vCardEmailField = values.vCardFields.find((f) => f.name === VCardFieldName.EMAIL);

		if (vCardEmailField && vCardEmailField?.contactAttribute === null) {
			errors.vCardFields = {};
			errors.vCardFields[values.vCardFields.indexOf(vCardEmailField)] = { contactAttribute: t("Field is required") };
		}

		return errors;
	};
};

export const ProfileSettingsPage: FC = () => {
	const { t } = useTranslation();
	const { data } = useQuery({
		queryKey: profileSettingsService.showKey,
		queryFn: () => profileSettingsService.show().then((r) => r.data.data),
	});
	const { showToast } = useToasts();
	const queryClient = useQueryClient();
	const validate = useValidate();

	const handleSubmit = async (values: ProfileSettingsResource) => {
		const response = await profileSettingsService.update({
			defaultContactViewId: values.defaultContactView.id,
			vCardFields: values.vCardFields.map((f) => ({
				name: f.name,
				contactAttributeId: f.contactAttribute?.id ?? null,
			})),
		});

		if (isSuccess(response)) {
			showToast({
				type: "success",
				title: t("Profile settings"),
				body: t("Profile settings were successfully updated."),
			});
			void queryClient.invalidateQueries({ queryKey: profileSettingsService.showKey });
			void queryClient.resetQueries({ queryKey: userService.indexKey });
			void queryClient.resetQueries({ queryKey: contactViewService.indexKey });
		}
	};

	return (
		<StyledCard>
			<CardHeader>
				<CardHeading>{t("Profile settings")}</CardHeading>
			</CardHeader>
			{data ? (
				<Formik initialValues={data} onSubmit={handleSubmit} validate={validate}>
					<ProfileSettingsForm initialValues={data} />
				</Formik>
			) : (
				<CardBody>
					<Loading />
				</CardBody>
			)}
		</StyledCard>
	);
};
